'use client';

import type { KnowledgeBaseDocumentWithRunInfo } from '@magicschool/knowledge-base';
import { logger } from '@magicschool/logger';
import { useQuery } from '@tanstack/react-query';
import { documentPollInterval } from '@/features/knowledge-base/documentIngestionStatus';

/**
 * Shared so the upload path can invalidate this list the moment a document is queued. Polling is
 * driven off the server's answer, so a newly uploaded document is invisible to it until one fetch
 * has seen the new row.
 */
export const toolDocumentsQueryKey = (customizationId: string | undefined) => ['tool-customization-documents', customizationId];

/**
 * Re-reads a chatbot's own documents while any of them is still being ingested.
 *
 * The drawer loads its file list once, server-side, on mount. Ingestion finishes minutes later, so
 * without this the list keeps showing whatever was true when the page rendered — which is how an
 * indexing document came to look finished. Polls on the interval shared with District Knowledge and
 * the Resource Library, and stops on the same stuck-run bound.
 */
export const useToolDocumentStatuses = (customizationId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: toolDocumentsQueryKey(customizationId),
    queryFn: async () => {
      const res = await fetch(`/api/tool-customizations/${customizationId}/documents?action=list`);
      if (!res.ok) {
        // When this read stops working the file list silently freezes on whatever the server rendered
        // at mount — the exact fault this poll exists to prevent, and indistinguishable from a list
        // that is simply up to date. Say so, so a stuck client is traceable to a failing route.
        logger.error('Failed to list chatbot documents while polling ingestion status', {
          customizationId,
          status: res.status,
        });
        throw new Error('Failed to list chatbot documents');
      }
      const { documents } = (await res.json()) as { documents: KnowledgeBaseDocumentWithRunInfo[] };
      return documents;
    },
    enabled: enabled && Boolean(customizationId),
    refetchInterval: (query) => documentPollInterval(query.state.data),
  });
